import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../common/Header/Header";
import Footer from "../../common/Footer/Footer";
import SEO from "../../components/SEO/SEO";
import { getLandingPage } from "./landingPageConfigs";

const SITE_URL = "https://mindtherapycounselling.com";

const TherapyLandingPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const page = getLandingPage(slug);
  const [openFaq, setOpenFaq] = useState(null);

  if (!page) {
    navigate("/", { replace: true });
    return null;
  }

  const expertsUrl = page.filterBranch
    ? `/experts?branchName=${encodeURIComponent(page.filterBranch)}`
    : "/experts";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: page.title,
    description: page.metaDescription,
    url: `${SITE_URL}/therapy/${slug}`,
    about: {
      "@type": "MedicalCondition",
      name: page.title,
    },
    provider: {
      "@type": "Organization",
      name: "MindTherapy Counselling",
      url: SITE_URL,
    },
  };

  return (
    <div className="bg-white">
      <SEO
        title={page.title}
        description={page.metaDescription}
        canonical={`${SITE_URL}/therapy/${slug}`}
      />
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      <Header />

      {/* Hero */}
      <section
        className="relative pt-36 pb-24 px-6 text-center overflow-hidden"
        style={{ minHeight: "520px" }}
      >
        {/* Background image with overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${page.heroImage})` }}
        />
        <div className="absolute inset-0" style={{ background: "rgba(30,20,50,0.38)" }} />

        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-6 drop-shadow-lg">
            {page.headline[0]}
            <br />
            <span className="text-purple-200">{page.headline[1]}</span>
          </h1>
          <p className="text-lg text-purple-100 mb-10 leading-relaxed drop-shadow">
            {page.heroSubtitle}
          </p>
          <button
            onClick={() => navigate(expertsUrl)}
            className="text-white text-lg font-semibold px-10 py-4 rounded-full hover:opacity-90 transition-opacity shadow-lg"
            style={{ backgroundColor: page.color }}
          >
            Book a Session
          </button>
        </div>
      </section>

      {/* What is */}
      <section className="py-16 px-6 max-w-4xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-6">
          {page.whatIs.heading}
        </h2>
        {page.whatIs.body.map((para, i) => (
          <p key={i} className="text-gray-700 leading-relaxed mb-4 text-base sm:text-lg">
            {para}
          </p>
        ))}
      </section>

      {/* Symptoms */}
      <section className="py-16 px-6" style={{ backgroundColor: "#f4efea" }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-8">
            {page.symptoms.heading}
          </h2>
          <ul className="grid sm:grid-cols-2 gap-4">
            {page.symptoms.items.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span
                  className="mt-1 w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: "#625480" }}
                >
                  ✓
                </span>
                <span className="text-gray-700 text-base">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How we help — split layout */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-12 items-center">
          <div className="flex-1">
            <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-6">
              {page.howWeHelp.heading}
            </h2>
            {page.howWeHelp.body.map((para, i) => (
              <p key={i} className="text-gray-700 leading-relaxed mb-4 text-base sm:text-lg">
                {para}
              </p>
            ))}
          </div>
          <div className="flex-shrink-0 w-full lg:w-[420px]">
            <img
              src={page.sectionImage}
              alt={page.howWeHelp.heading}
              className="rounded-2xl shadow-xl w-full object-cover"
              style={{ maxHeight: "480px" }}
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section
        className="py-16 px-6 text-center"
        style={{ background: "linear-gradient(135deg, #625480 0%, #9b7ab8 100%)" }}
      >
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
          Ready to Take the First Step?
        </h2>
        <p className="text-purple-100 mb-8 text-lg max-w-xl mx-auto">
          Connect with a licensed therapist who specialises in {page.title.toLowerCase()}. Your first session could change everything.
        </p>
        <button
          onClick={() => navigate(expertsUrl)}
          className="bg-white font-semibold text-lg px-10 py-4 rounded-full hover:bg-gray-100 transition-colors shadow-md"
          style={{ color: "#625480" }}
        >
          Book a Session
        </button>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 max-w-4xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {page.faq.map((item, i) => (
            <div
              key={i}
              className="border border-gray-200 rounded-xl overflow-hidden"
            >
              <button
                className="w-full text-left px-6 py-4 flex justify-between items-center font-semibold text-gray-800 hover:bg-gray-50 transition-colors"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span>{item.q}</span>
                <span className="text-2xl text-primary ml-4 flex-shrink-0">
                  {openFaq === i ? "−" : "+"}
                </span>
              </button>
              {openFaq === i && (
                <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-100">
                  <p className="pt-4">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TherapyLandingPage;
